import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import {
  DATE_RANGE_PRESETS,
  formatForQueryParam,
  parseStartDate,
  parseEndDate,
  todayAsString,
} from 'garaje/utils/date-range-helper';

// ── Step-group types ───────────────────────────────────────────────────────
export interface CascadeSubstep {
  label: string;
  status: string;
  timestamp?: string;
  isActive?: boolean;
}

export interface CascadeStepLeaf {
  type: 'leaf';
  id: string;
  label: string;
  status: string;
  timestamp: string;
  isActive: boolean;
}

export interface CascadeStepGroupItem {
  type: 'group';
  id: string;
  label: string;
  status: string;
  summary: string;
  isActive: boolean;
  defaultOpen: boolean;
  substeps: CascadeSubstep[];
}

export type CascadeStepItem = CascadeStepLeaf | CascadeStepGroupItem;

// ── Module-level helpers ───────────────────────────────────────────────────
const LEAF_KINDS = new Set([
  'CASCADE_ENQUEUED',
  'CASCADE_STARTED',
  'CASCADE_COMPLETED',
  'CASCADE_FAILED',
  'CASCADE_CANCELLED',
]);

function formatStepTime(iso: string, tz: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const timeStr = d.toLocaleTimeString('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const abbr =
    new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value ?? '';
  return abbr ? `${timeStr} ${abbr}` : timeStr;
}

interface AuditEntry {
  id: string;
  routine_id: string;
  audit_event_kind: string;
  activity: string;
  status: string;
  timestamp: string;
  integration?: string;
  location?: string;
  employee_name?: string;
  employee_email?: string;
  employee_hris_id?: string;
  lifecycle_type?: string;
  mode?: string;
  attempt_number?: number;
  initiated_by?: string;
  grace_period?: string;
  grace_period_end?: string;
  acs_cardholder_external_id?: string;
  blocklist_entry_id?: string;
  event_id?: string;
  reason?: string;
  fail_reason?: string;
  before_state?: unknown;
  after_state?: unknown;
  vendor_error?: unknown;
  cascade_status?: string;
  cascade_steps?: Array<{ id: string; activity: string; status: string }>;
}

interface ColumnToggleKeys {
  colIntegration: boolean;
  colLocation: boolean;
  colMode: boolean;
  colAttempt: boolean;
  colInitiatedBy: boolean;
  colGracePeriodEnd: boolean;
  colCardholderId: boolean;
  colEventId: boolean;
}

function substepLabel(entry: AuditEntry): string {
  const parts: string[] = [];
  if (entry.integration) parts.push(entry.integration);
  if (entry.location) parts.push(entry.location);
  return parts.length > 0 ? parts.join(' @ ') : 'Global';
}

function aggregateSubsteps(substeps: CascadeSubstep[]): { status: string; summary: string } {
  const succeeded = substeps.filter((s) => s.status === 'SUCCESS').length;
  const failed = substeps.filter((s) => s.status === 'FAILURE' || s.status === 'PARTIAL_FAILURE').length;
  const skipped = substeps.filter((s) => s.status === 'SKIPPED' || s.status === 'SKIPPED_NO_MATCH').length;

  let status = 'SUCCESS';
  if (failed > 0 && succeeded > 0) status = 'PARTIAL_FAILURE';
  else if (failed > 0) status = 'FAILURE';
  else if (succeeded === 0 && skipped > 0) status = 'SKIPPED';

  const parts: string[] = [];
  if (succeeded) parts.push(`${succeeded} succeeded`);
  if (failed) parts.push(`${failed} failed`);
  if (skipped) parts.push(`${skipped} skipped`);
  return { status, summary: parts.join(' · ') };
}

const ACTIVITY_OPTIONS = [
  { value: 'CASCADE_ENQUEUED', label: 'Routine enqueued' },
  { value: 'CASCADE_STARTED', label: 'Routine started' },
  { value: 'acs', label: 'Deprovision ACS credentials' },
  { value: 'blocklist', label: 'Add to visitor blocklist' },
  { value: 'forceSignout', label: 'Force sign-out' },
  { value: 'removeProfile', label: 'Remove employee profile' },
  { value: 'CASCADE_COMPLETED', label: 'Routine completed' },
];

const STATUS_OPTIONS = [
  { value: 'SUCCESS', label: 'Success' },
  { value: 'FAILURE', label: 'Failure' },
  { value: 'PARTIAL_FAILURE', label: 'Partial failure' },
  { value: 'SKIPPED', label: 'Skipped' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export default class LocationOverviewHrisAuditLogController extends Controller {
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;

  queryParams = [
    'page',
    'pageSize',
    'query',
    'sortBy',
    'sortDirection',
    'eventType',
    'status',
    'locationIds',
    'startDate',
    'endDate',
    'integration',
  ];

  @tracked page = 1;
  @tracked pageSize = 25;
  @tracked query: string | undefined;
  @tracked sortBy: string | undefined;
  @tracked sortDirection: string | undefined;
  @tracked eventType: string | undefined;
  @tracked status: string | undefined;
  @tracked locationIds: string | undefined;
  @tracked startDate: string | undefined;
  @tracked endDate: string | undefined;

  @tracked selectedEntry: AuditEntry | null = null;
  @tracked selectedEntryIndex: number | null = null;

  @tracked selectedActivities: { value: string; label: string }[] = [];

  @tracked selectedLocationIds: string[] | null = null;

  @tracked selectedIntegrations: string[] | null = null;
  @tracked integration: string | undefined;

  // Column visibility — first 4 cols are locked (always shown)
  @tracked showColumnsMenu = false;
  @tracked colIntegration = true;
  @tracked colLocation = true;
  @tracked colMode = true;
  @tracked colAttempt = true;
  @tracked colInitiatedBy = true;
  @tracked colGracePeriodEnd = true;
  @tracked colCardholderId = true;
  @tracked colEventId = true;

  activityOptions = ACTIVITY_OPTIONS;
  statusOptions = STATUS_OPTIONS;
  dateRangeFilterOptions = DATE_RANGE_PRESETS;

  get entries(): AuditEntry[] {
    return (this.model as { entries?: AuditEntry[] })?.entries ?? [];
  }

  get meta(): { total: number } {
    return (this.model as { meta?: { total: number } })?.meta ?? { total: 0 };
  }

  get pageCount(): number {
    return Math.ceil(this.meta.total / this.pageSize);
  }

  get visibleColumnCount(): number {
    return (
      4 + // locked: timestamp, status, employee, activity
      (this.colIntegration ? 1 : 0) +
      (this.colLocation ? 1 : 0) +
      (this.colMode ? 1 : 0) +
      (this.colAttempt ? 1 : 0) +
      (this.colInitiatedBy ? 1 : 0) +
      (this.colGracePeriodEnd ? 1 : 0) +
      (this.colCardholderId ? 1 : 0) +
      (this.colEventId ? 1 : 0)
    );
  }

  get timezone(): string {
    return this.state.currentLocation?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  get start(): Date {
    return parseStartDate(this.startDate ?? null, todayAsString(this.timezone), this.timezone);
  }

  get end(): Date {
    return parseEndDate(this.endDate ?? null, todayAsString(this.timezone), this.timezone);
  }

  get allTimeMin(): Date {
    return modifyDateInTimeZone(this.state.currentLocation.createdAt, this.timezone, (d) => d);
  }

  get isFiltered(): boolean {
    return !!(
      this.eventType ||
      this.status ||
      this.locationIds ||
      this.startDate ||
      this.endDate ||
      this.query ||
      this.selectedActivities.length > 0 ||
      this.selectedLocationIds !== null ||
      this.selectedIntegrations !== null
    );
  }

  get isGlobal(): boolean {
    return (this.model as { isGlobal?: boolean })?.isGlobal ?? false;
  }

  get hasPrev(): boolean {
    return (this.selectedEntryIndex ?? 0) > 0;
  }

  get hasNext(): boolean {
    return (this.selectedEntryIndex ?? 0) < this.entries.length - 1;
  }

  get cascadeStepsUpToCurrent(): Array<{ id: string; activity: string; status: string }> {
    const steps = this.selectedEntry?.cascade_steps ?? [];
    const currentId = this.selectedEntry?.id;
    const idx = steps.findIndex((s) => s.id === currentId);
    return idx >= 0 ? steps.slice(0, idx + 1) : steps;
  }

  get cascadeStepGroups(): CascadeStepItem[] {
    if (!this.selectedEntry) return [];
    const routineId = this.selectedEntry.routine_id;
    const currentId = this.selectedEntry.id;
    const tz = this.timezone;

    const allEntries = this.entries.filter((e) => e.routine_id === routineId);

    const result: CascadeStepItem[] = [];
    const groupIndex: Record<string, CascadeStepGroupItem> = {};

    for (const entry of allEntries) {
      if (LEAF_KINDS.has(entry.audit_event_kind)) {
        result.push({
          type: 'leaf',
          id: entry.id,
          label: entry.activity,
          status: entry.status,
          timestamp: formatStepTime(entry.timestamp, tz),
          isActive: entry.id === currentId,
        });
      } else if (entry.audit_event_kind === 'STEP_COMPLETED') {
        const key = entry.activity;
        if (!groupIndex[key]) {
          groupIndex[key] = {
            type: 'group',
            id: entry.id,
            label: key,
            status: 'SUCCESS',
            summary: '',
            isActive: false,
            defaultOpen: false,
            substeps: [],
          };
          result.push(groupIndex[key]);
        }
        const group = groupIndex[key];
        group.substeps.push({
          label: substepLabel(entry),
          status: entry.status,
          timestamp: formatStepTime(entry.timestamp, tz),
          isActive: entry.id === currentId,
        });
        if (entry.id === currentId) {
          group.isActive = true;
          group.defaultOpen = true;
        }
      }
    }

    // Compute aggregate status + summary for each group
    for (const group of Object.values(groupIndex)) {
      const agg = aggregateSubsteps(group.substeps);
      group.status = agg.status;
      group.summary = agg.summary;
    }

    return result;
  }

  get selectedEntryForDrawer():
    | (Omit<AuditEntry, 'before_state' | 'after_state' | 'vendor_error'> & {
        before_state: string | null;
        after_state: string | null;
        vendor_error: string | null;
      })
    | null {
    const e = this.selectedEntry;
    if (!e) return null;
    const serialize = (v: unknown) => (v == null ? null : typeof v === 'string' ? v : JSON.stringify(v, null, 2));
    return {
      ...e,
      before_state: serialize(e.before_state),
      after_state: serialize(e.after_state),
      vendor_error: serialize(e.vendor_error),
    };
  }

  get locationOptions(): { value: string; label: string }[] {
    const locations = (this.model as { locations?: Array<{ id: string | number; name?: string }> })?.locations ?? [];
    return locations.map((l) => ({ value: String(l.id), label: l.name ?? String(l.id) }));
  }

  get integrationOptions(): { value: string; label: string }[] {
    const integrations = (this.model as { integrations?: string[] })?.integrations ?? [];
    return integrations.map((name) => ({ value: name, label: name }));
  }

  get currentSelectedStatuses(): { value: string; label: string }[] {
    if (!this.status) return [];
    return this.status.split(',').flatMap((v) => {
      const opt = STATUS_OPTIONS.find((o) => o.value === v.trim());
      return opt ? [opt] : [];
    });
  }

  @action setPage(page: number): void {
    this.page = page;
  }

  @action setQuery(value: string | undefined): void {
    this.query = value;
    this.page = 1;
  }

  @action setSortBy(col: string): void {
    if (this.sortBy === col) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = col;
      this.sortDirection = 'asc';
    }
    this.page = 1;
  }

  @action setEventType(value: string | undefined): void {
    this.eventType = value === 'All events' ? undefined : value;
    this.page = 1;
  }

  @action setStatus(value: string | undefined): void {
    this.status = !value ? undefined : value;
    this.page = 1;
  }

  @action setLocationIds(value: string | undefined): void {
    this.locationIds = value;
    this.page = 1;
  }

  @action setSelectedStatuses(opts: { value: string; label: string }[]): void {
    this.status = opts.length ? opts.map((o) => o.value).join(',') : undefined;
    this.page = 1;
  }

  @action selectDateRange(startDate: Date, endDate: Date): void {
    this.startDate = formatForQueryParam(startDate, this.timezone);
    this.endDate = formatForQueryParam(endDate, this.timezone);
    this.page = 1;
  }

  @action toggleColumnsMenu(): void {
    this.showColumnsMenu = !this.showColumnsMenu;
  }

  @action closeColumnsMenu(): void {
    this.showColumnsMenu = false;
  }

  @action toggleColumn(col: string): void {
    (this as unknown as ColumnToggleKeys)[col as keyof ColumnToggleKeys] = !(this as unknown as ColumnToggleKeys)[
      col as keyof ColumnToggleKeys
    ];
  }

  @action selectEntry(entry: AuditEntry, idx: number): void {
    this.selectedEntry = entry;
    this.selectedEntryIndex = idx;
    const hasScrollBar = window.innerWidth > document.body.clientWidth;
    if (hasScrollBar) {
      document.body.style.marginRight = `${window.innerWidth - document.body.clientWidth}px`;
    }
    document.body.classList.add('overflow-hidden');
  }

  @action closePanel(): void {
    this.selectedEntry = null;
    this.selectedEntryIndex = null;
    document.body.classList.remove('overflow-hidden');
    document.body.style.marginRight = '';
  }

  @action navigatePrev(): void {
    const idx = this.selectedEntryIndex;
    if (idx === null || idx <= 0) return;
    this.selectedEntryIndex = idx - 1;
    this.selectedEntry = this.entries[idx - 1] ?? null;
  }

  @action navigateNext(): void {
    const idx = this.selectedEntryIndex;
    if (idx === null || idx >= this.entries.length - 1) return;
    this.selectedEntryIndex = idx + 1;
    this.selectedEntry = this.entries[idx + 1] ?? null;
  }

  @action selectCascadeStep(stepId: string): void {
    const entries = this.entries;
    const idx = entries.findIndex((e) => e.id === stepId);
    if (idx >= 0) {
      this.selectedEntry = entries[idx] ?? null;
      this.selectedEntryIndex = idx;
    }
  }

  @action copyField(value: string): void {
    try {
      void navigator.clipboard?.writeText(value);
      this.flashMessages.showAndHideFlash('success', 'Copied to clipboard');
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Failed to copy');
    }
  }

  @action setSelectedActivities(opts: { value: string; label: string }[]): void {
    this.selectedActivities = opts;
    this.page = 1;
  }

  @action setSelectedLocationIds(value: string[] | null): void {
    this.selectedLocationIds = value && value.length ? value : null;
    this.locationIds = value && value.length ? value.join(',') : undefined;
    this.page = 1;
  }

  @action setSelectedIntegrations(value: string[] | null): void {
    this.selectedIntegrations = value && value.length ? value : null;
    this.integration = value && value.length ? value.join(',') : undefined;
    this.page = 1;
  }

  @action copyEventId(value: string, e?: Event): void {
    e?.stopPropagation();
    try {
      void navigator.clipboard?.writeText(value);
      this.flashMessages.showAndHideFlash('success', 'Copied to clipboard');
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Failed to copy');
    }
  }

  @action exportCsv(): void {
    const cols = [
      'timestamp',
      'status',
      'employee_name',
      'activity',
      'integration',
      'location',
      'mode',
      'attempt_number',
      'initiated_by',
      'grace_period_end',
      'acs_cardholder_external_id',
      'event_id',
    ];
    const esc = (v: unknown) => {
      const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v as string | number | boolean);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const lines = [cols.join(',')];
    for (const row of this.entries) {
      lines.push(cols.map((c) => esc((row as unknown as Record<string, unknown>)[c])).join(','));
    }
    try {
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit-log.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 500);
      this.flashMessages.showAndHideFlash('success', `Exported ${this.entries.length} rows`);
    } catch (_) {
      this.flashMessages.showAndHideFlash('error', 'Export failed');
    }
  }

  @action clearFilters(): void {
    this.eventType = undefined;
    this.status = undefined;
    this.locationIds = undefined;
    this.startDate = undefined;
    this.endDate = undefined;
    this.query = undefined;
    this.selectedActivities = [];
    this.selectedLocationIds = null;
    this.selectedIntegrations = null;
    this.integration = undefined;
    this.page = 1;
  }
}
